/**
 * This is the "TOP" page.
 */

import { Divider, VStack } from '@chakra-ui/layout';
import { Skeleton, Text, useDisclosure } from '@chakra-ui/react';
import React from 'react';
import { Navigate } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { ListUtils } from '../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import StyledMessageList from '../../../components/StyledMessageList';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { usePostDeadlineSearch } from '../../../hooks/queries/deadline';
import { usePostNoticeSearchPolling } from '../../../hooks/queries/notice';
import { usePostWorkerPersonalInfoAgree } from '../../../hooks/queries/worker';
import useToastHook from '../../../hooks/ToastHook';
import { DeadlineCategory } from '../../../models/gen/deadlineCategory';
import { DeadlineMode } from '../../../models/gen/deadlineMode';
import { Notice } from '../../../models/gen/models';
import DeadlineUtils from '../../../utils/DeadlineUtils';
import { useMasterContext } from '../../MasterProvider';
import { NotificationDetailModal } from '../../modals/NotificationDetail';
import { useSizeContext } from '../../SizeProvider';
import Wireframe from '../../wireframe/Wireframe';
import { ConsentModal } from './ConsentModal';

const Top: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    /* UserInfoAccessor.getUserInfo() */
    console.error('no userInfo');
    return <Navigate to={pagePaths.ROOT} />;
  }

  const master = useMasterContext();
  const sizes = useSizeContext();
  const { toast } = useToastHook();
  const postWorkerPersonalInfoAgree = usePostWorkerPersonalInfoAgree();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedNoticeId, setSelectedNoticeId] = React.useState<number>();

  const {
    data: deadlines,
    isLoading: isLoadingDeadlines,
    refetch: refetchDeadline,
  } = usePostDeadlineSearch(
    { modes: [DeadlineMode.All] },
    TypeUtils.isNonNilNumber(
      state.appConfig.applicationSettings?.POLLING_INTERVAL
    )
      ? state.appConfig.applicationSettings!.POLLING_INTERVAL
      : undefined
  );
  const {
    data: notices,
    isLoading: isLoadingNotices,
    refetch: refetchNotice,
  } = usePostNoticeSearchPolling(
    {
      isOutOfPeriodInclude: false,
    },
    TypeUtils.isNonNilNumber(
      state.appConfig.applicationSettings?.POLLING_INTERVAL
    )
      ? state.appConfig.applicationSettings!.POLLING_INTERVAL
      : undefined
  );
  React.useState(() => {
    refetchDeadline();
    refetchNotice();
  });

  const hasDelayed = React.useMemo(
    () =>
      deadlines?.results?.some(
        (result) => result.category === DeadlineCategory.Delayed
      ),
    [deadlines]
  );
  const daily = React.useMemo(
    () =>
      deadlines?.results?.filter((result) => DeadlineUtils.isDaily(result)) ||
      [],
    [deadlines]
  );
  const monthly = React.useMemo(
    () =>
      deadlines?.results?.filter((result) => DeadlineUtils.isMonthly(result)) ||
      [],
    [deadlines]
  );
  const noticeMap = React.useMemo(
    () =>
      notices?.results?.reduce((prev, cur) => {
        if (TypeUtils.isNilNumber(cur.noticeCategoryId)) {
          return prev;
        }

        return {
          ...prev,
          [cur.noticeCategoryId]: [...(prev[cur.noticeCategoryId] || []), cur],
        };
      }, {} as Record<number, Array<Notice>>) || {},
    [notices]
  );

  return (
    <Wireframe title={pageNames.TOP}>
      <ConsentModal
        isOpen={state.agreeRequired}
        onClose={() => null}
        onSubmit={() => {
          // actions.setGuard(true);
          postWorkerPersonalInfoAgree.mutate(
            {},
            {
              onSuccess: () => {
                actions.setAgreeRequired(false);
                // actions.setGuard(false);
              },
              onError: (error) => {
                toast(
                  error instanceof AppError
                    ? error
                    : 'お手数ですがもう一度お試しください'
                );
                // actions.setGuard(false);
              },
            }
          );
        }}
      />

      <Skeleton
        as={VStack}
        isLoaded={!isLoadingDeadlines && !isLoadingNotices}
        width="100%"
      >
        <Text
          color="red"
          fontWeight="bold"
          fontSize={sizes.fontSize.rawMessage}
          hidden={!hasDelayed}
        >
          警告：期限超過のものがあります。速やかに対処してください。
        </Text>
        <Divider borderColor="gray" hidden={hasDelayed} />
        <Text fontSize={sizes.fontSize.rawHeader} fontWeight="bold">
          やること TODO
        </Text>
        {ListUtils.isEmpty(daily) && ListUtils.isEmpty(monthly) ? (
          <Text fontSize={sizes.fontSize.rawMessage}>やることはありません</Text>
        ) : (
          <>
            <StyledMessageList title="日次 daily" array={daily} />
            <StyledMessageList title="月次 monthly" array={monthly} />
          </>
        )}
        {Object.values(master.noticeCategory)
          .sort(ListUtils.sort((noticeCategory) => noticeCategory.displayOrder))
          .filter((noticeCategory) =>
            ListUtils.isNotEmpty(noticeMap[noticeCategory.id])
          )
          .map((noticeCategory) => (
            <>
              <Text fontSize={sizes.fontSize.rawHeader} fontWeight="bold">
                {noticeCategory.name}
              </Text>
              <StyledMessageList
                array={noticeMap[noticeCategory.id]}
                onOpen={(noticeId) => {
                  setSelectedNoticeId(noticeId);
                  onOpen();
                }}
              />
            </>
          ))}
      </Skeleton>
      <NotificationDetailModal
        isOpen={isOpen}
        noticeId={selectedNoticeId}
        onClose={onClose}
      />
    </Wireframe>
  );
};

export default Top;
